import React, { useEffect, useState } from 'react';
import Navbar from '../components/navbar';
import Footer from '../components/footer';
import { UserPlus, Send, CheckCircle2, Briefcase, ChevronDown, Check } from 'lucide-react';

const INITIAL_FORM = {
  name: '',
  companyName: '',
  city_id: '',
  chapter_id: '',
  bussinessCatagory: '',
  customBusinessCategory: '',
  email: '',
  phone: '',
  invitedBy: '',
};

const INDUSTRY_OPTIONS = [
  'Manufacturing',
  'Trading & Export',
  'IT & Technology',
  'Healthcare & Medical',
  'Real Estate & Construction',
  'Finance & Insurance',
  'Education & Training',
  'Hospitality & Restaurant',
  'Retail & E-commerce',
  'Legal & Consultancy',
  'Marketing & Advertising',
  'Logistics & Transport',
  'Agriculture & Food',
  'Textile & Garments',
  'Automobile & Engineering',
  'Other',
];

const VisitorForm = () => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [formData, setFormData] = useState({ ...INITIAL_FORM });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [cities, setCities] = useState([]);
  const [chapters, setChapters] = useState([]);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/cities`);
        const result = await response.json();
        if (result.success && Array.isArray(result.data)) {
          setCities(result.data);
        }
      } catch (error) {
        console.error('Error fetching cities:', error);
      }
    };
    fetchCities();
  }, [API_BASE_URL]);

  useEffect(() => {
    if (!formData.city_id) {
      setChapters([]);
      return;
    }

    const fetchChapters = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/chapters/${formData.city_id}`);
        const result = await response.json();
        if (result.success && Array.isArray(result.data)) {
          setChapters(result.data);
        } else {
          setChapters([]);
        }
      } catch (error) {
        console.error('Error fetching chapters:', error);
        setChapters([]);
      }
    };
    fetchChapters();
  }, [API_BASE_URL, formData.city_id]);

  useEffect(() => {
    if (!categoryOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [categoryOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'bussinessCatagory') {
      setFormData((prev) => ({
        ...prev,
        bussinessCatagory: value,
        customBusinessCategory: value === 'Other' ? prev.customBusinessCategory : '',
      }));
    } else if (name === 'city_id') {
      setFormData((prev) => ({
        ...prev,
        city_id: value,
        chapter_id: '',
      }));
    } else if (name === 'phone') {
      setFormData((prev) => ({
        ...prev,
        phone: value.replace(/\D/g, '').slice(0, 10),
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    if (submitError) setSubmitError('');
  };

  const handleCategorySelect = (value) => {
    setFormData((prev) => ({
      ...prev,
      bussinessCatagory: value,
      customBusinessCategory: value === 'Other' ? prev.customBusinessCategory : '',
    }));
    setCategoryOpen(false);

    if (errors.bussinessCatagory || errors.customBusinessCategory) {
      setErrors((prev) => ({
        ...prev,
        bussinessCatagory: '',
        customBusinessCategory: '',
      }));
    }
    if (submitError) setSubmitError('');
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.name.trim()) nextErrors.name = 'Name is required';
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      nextErrors.email = 'Enter a valid email address';
    }
    if (!formData.phone.trim()) {
      nextErrors.phone = 'Phone number is required';
    } else if (!/^[0-9]{10}$/.test(formData.phone.trim())) {
      nextErrors.phone = 'Enter a valid 10 digit phone number';
    }

    if (formData.bussinessCatagory === 'Other' && !formData.customBusinessCategory.trim()) {
      nextErrors.customBusinessCategory = 'Please specify your business category';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError('');

    const finalBusinessCategory =
      formData.bussinessCatagory === 'Other'
        ? formData.customBusinessCategory.trim()
        : formData.bussinessCatagory;

    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      companyName: formData.companyName.trim(),
      bussinessCatagory: finalBusinessCategory,
      invitedBy: formData.invitedBy.trim(),
      ...(formData.city_id && { city_id: formData.city_id }),
      ...(formData.chapter_id && { chapter_id: formData.chapter_id }),
    };

    try {
      const response = await fetch(`${API_BASE_URL}/visitors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.success) {
        setIsSubmitted(true);
        setFormData({ ...INITIAL_FORM });
      } else {
        setSubmitError(result.message || 'Failed to submit visitor form. Please try again.');
      }
    } catch (error) {
      console.error('Visitor form error:', error);
      setSubmitError('An error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `w-full min-h-[48px] px-3 sm:px-4 py-3 text-base border rounded-xl outline-none transition-all box-border ${
      errors[field]
        ? 'border-red-400 focus:ring-2 focus:ring-red-200'
        : 'border-gray-300 focus:ring-2 focus:ring-[#FB9724]'
    }`;

  const labelClass = 'block text-gray-700 font-medium mb-2 text-sm sm:text-base';

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-gradient-to-b from-slate-50 to-white font-sans">
      <Navbar scrollToSection={() => {}} />

      <div className="bg-gradient-to-r from-[#1A87CF] to-[#1565a0] text-white pt-24 sm:pt-28 md:pt-32 pb-10 sm:pb-12 md:pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white/15 mb-4 sm:mb-5">
            <UserPlus className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold font-rubik mb-3 sm:mb-4 leading-tight px-1">
            Visitor Registration
          </h1>
          <p className="text-white/85 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed px-1">
            Planning to attend an ABN chapter meeting as a guest? Fill in your details below and our team will connect with you.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto w-full px-3 sm:px-6 py-6 sm:py-10 md:py-14 -mt-4 sm:-mt-6">
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl border border-gray-100 p-4 sm:p-6 md:p-10 w-full">
          {isSubmitted ? (
            <div className="text-center py-6 sm:py-10 px-1">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-5 sm:mb-6">
                <CheckCircle2 className="w-8 h-8 sm:w-10 sm:h-10 text-green-500" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 font-rubik">
                Registration Submitted!
              </h2>
              <p className="text-gray-600 mb-6 sm:mb-8 max-w-md mx-auto text-sm sm:text-base">
                Thank you for registering as a visitor. Our chapter team will review your details and get in touch with you shortly.
              </p>
              <button
                type="button"
                onClick={() => setIsSubmitted(false)}
                className="w-full sm:w-auto bg-[#1A87CF] text-white px-6 sm:px-8 py-3 rounded-xl font-semibold hover:bg-[#1569A3] transition-colors"
              >
                Submit Another Response
              </button>
            </div>
          ) : (
            <form className="space-y-4 sm:space-y-6" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div className="min-w-0">
                  <label className={labelClass}>Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={inputClass('name')}
                    placeholder="Enter your full name"
                  />
                  {errors.name && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.name}</p>}
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>Company Name</label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    className={inputClass('companyName')}
                    placeholder="Your company name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div className="min-w-0">
                  <label className={labelClass}>City</label>
                  <select
                    name="city_id"
                    value={formData.city_id}
                    onChange={handleChange}
                    className={inputClass('city_id')}
                  >
                    <option value="">Select city</option>
                    {cities.map((city) => (
                      <option key={city._id} value={city._id}>
                        {city.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>Chapter</label>
                  <select
                    name="chapter_id"
                    value={formData.chapter_id}
                    onChange={handleChange}
                    className={inputClass('chapter_id')}
                    disabled={!formData.city_id}
                  >
                    <option value="">Select chapter</option>
                    {chapters.map((chapter) => (
                      <option key={chapter._id} value={chapter._id}>
                        {chapter.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div className="min-w-0 relative z-20">
                  <label className={`${labelClass} flex items-center gap-1.5`}>
                    <Briefcase className="w-4 h-4 text-[#FB9724] shrink-0" />
                    <span>Business Category</span>
                  </label>

                  <button
                    type="button"
                    onClick={() => setCategoryOpen((prev) => !prev)}
                    className={`${inputClass('bussinessCatagory')} text-left flex items-center justify-between gap-3`}
                    aria-expanded={categoryOpen}
                    aria-haspopup="listbox"
                  >
                    <span className={`truncate ${formData.bussinessCatagory ? 'text-gray-900' : 'text-gray-400'}`}>
                      {formData.bussinessCatagory || 'Select category (optional)'}
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 text-gray-400 shrink-0 transition-transform ${categoryOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {categoryOpen && (
                    <>
                      <button
                        type="button"
                        aria-label="Close category list"
                        className="fixed inset-0 z-40 bg-black/40 md:bg-transparent"
                        onClick={() => setCategoryOpen(false)}
                      />

                      <div
                        role="listbox"
                        className="fixed inset-x-0 bottom-0 z-50 max-h-[70vh] overflow-hidden rounded-t-2xl border border-gray-200 bg-white shadow-2xl md:absolute md:inset-x-0 md:bottom-auto md:top-full md:mt-2 md:max-h-64 md:rounded-xl"
                      >
                        <div className="border-b border-gray-100 px-4 py-3 md:hidden">
                          <p className="text-sm font-semibold text-gray-900">Select Business Category</p>
                        </div>

                        <div
                          className="max-h-[calc(70vh-56px)] overflow-y-auto overscroll-contain md:max-h-64"
                          style={{ WebkitOverflowScrolling: 'touch' }}
                        >
                          <button
                            type="button"
                            role="option"
                            aria-selected={!formData.bussinessCatagory}
                            onClick={() => handleCategorySelect('')}
                            className={`flex w-full items-center justify-between px-4 py-3.5 text-left text-base border-b border-gray-50 hover:bg-orange-50 ${
                              !formData.bussinessCatagory ? 'bg-orange-50 text-[#FB9724] font-medium' : 'text-gray-700'
                            }`}
                          >
                            <span>Select category (optional)</span>
                            {!formData.bussinessCatagory && <Check className="w-4 h-4 shrink-0" />}
                          </button>

                          {INDUSTRY_OPTIONS.map((option) => (
                            <button
                              key={option}
                              type="button"
                              role="option"
                              aria-selected={formData.bussinessCatagory === option}
                              onClick={() => handleCategorySelect(option)}
                              className={`flex w-full items-center justify-between px-4 py-3.5 text-left text-base border-b border-gray-50 last:border-b-0 hover:bg-orange-50 active:bg-orange-100 ${
                                formData.bussinessCatagory === option
                                  ? 'bg-orange-50 text-[#FB9724] font-medium'
                                  : 'text-gray-700'
                              }`}
                            >
                              <span className="pr-3">{option}</span>
                              {formData.bussinessCatagory === option && (
                                <Check className="w-4 h-4 shrink-0" />
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {formData.bussinessCatagory === 'Other' && (
                    <input
                      type="text"
                      name="customBusinessCategory"
                      value={formData.customBusinessCategory}
                      onChange={handleChange}
                      className={`${inputClass('customBusinessCategory')} mt-3`}
                      placeholder="Enter your business category"
                    />
                  )}
                  {errors.customBusinessCategory && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.customBusinessCategory}</p>
                  )}
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>Invited By</label>
                  <input
                    type="text"
                    name="invitedBy"
                    value={formData.invitedBy}
                    onChange={handleChange}
                    className={inputClass('invitedBy')}
                    placeholder="Member name who invited you"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div className="min-w-0">
                  <label className={labelClass}>Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    maxLength={10}
                    inputMode="numeric"
                    className={inputClass('phone')}
                    placeholder="98xxx xxxxx"
                  />
                  {errors.phone && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.phone}</p>}
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={inputClass('email')}
                    placeholder="your@email.com"
                  />
                  {errors.email && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.email}</p>}
                </div>
              </div>

              {submitError && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-3 sm:px-4 py-3 text-xs sm:text-sm text-red-600 break-words">
                  {submitError}
                </div>
              )}

              <div className="pt-1 sm:pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto sm:min-w-[220px] flex items-center justify-center gap-2 bg-[#FB9724] text-white px-6 sm:px-10 py-3.5 sm:py-4 rounded-xl text-base sm:text-lg font-semibold hover:bg-[#E0851F] transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-lg hover:shadow-xl mx-auto"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Registration'}
                  {!isSubmitting && <Send size={18} className="sm:w-5 sm:h-5" />}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      <Footer scrollToSection={() => {}} />
    </div>
  );
};

export default VisitorForm;
